import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  fetchSellers,
  requestDownloadSellers,
  fetchSellerOptions,
  fetchSellerWarehouseOptions,
  fetchCompanyWarehouseOptions,
  fetchOwnWarehouseOptions,
  createSeller,
  checkSellerEmail,
} from './sellersThunks';

const initialState = {
  /** Current page of the sellers table. */
  sellers: [],
  totalCount: 0,
  loading: false,
  error: null,
  /** The table's own failure: `error` is written by every read in here. */
  listError: null,
  filters: {
    search: "",
    page: 1,
    rowsPerPage: 20,
  },

  /** Whole roster as `{ value, label }`, for selects rather than the table. */
  sellerOptions: [],
  sellerOptionsLoaded: false,
  loadingSellerOptions: false,

  /** Warehouses of the seller currently picked in a form. */
  sellerWarehouseOptions: [],
  loadingSellerWarehouseOptions: false,

  /**
   * Creation wizard. Kept apart from `loading`, which drives the table: the
   * wizard is a different screen and must not blank the list behind it.
   *
   * `createdSeller` holds the whole 201 body, temporary password included —
   * the only moment that password is ever readable, so the success screen
   * shows it instead of throwing it away.
   */
  creating: false,
  createError: null,
  createdSeller: null,

  /** Availability of the email being typed. `available: null` means unknown. */
  emailCheck: {
    email: '',
    status: 'idle',
    available: null,
  },
};

const sellersSlice = createSlice({
  name: 'sellers',
  initialState,
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    /** Drops the warehouses of the previously selected seller. */
    clearSellerWarehouseOptions: (state) => {
      state.sellerWarehouseOptions = [];
    },
    /** Resets the wizard, so leaving and re-entering starts on a blank sheet. */
    clearSellerCreation: (state) => {
      state.creating = false;
      state.createError = null;
      state.createdSeller = null;
      state.emailCheck = { email: '', status: 'idle', available: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSellers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.listError = null;
      })
      .addCase(fetchSellers.fulfilled, (state, action) => {
        state.loading = false;
        state.sellers = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchSellers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.listError = action.payload;
      })

      // Seller roster for selects
      .addCase(fetchSellerOptions.pending, (state) => {
        state.loadingSellerOptions = true;
        state.error = null;
      })
      .addCase(fetchSellerOptions.fulfilled, (state, action) => {
        state.loadingSellerOptions = false;
        state.sellerOptionsLoaded = true;
        state.sellerOptions = action.payload;
      })
      .addCase(fetchSellerOptions.rejected, (state, action) => {
        state.loadingSellerOptions = false;
        if (action.payload) state.error = action.payload;
      })

      // Warehouses of a company, the door staff uses
      .addCase(fetchCompanyWarehouseOptions.pending, (state) => {
        state.loadingSellerWarehouseOptions = true;
        state.error = null;
      })
      .addCase(fetchCompanyWarehouseOptions.fulfilled, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = action.payload;
      })
      .addCase(fetchCompanyWarehouseOptions.rejected, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = [];
        if (action.payload) state.error = action.payload;
      })

      // Warehouses of the selected seller
      .addCase(fetchSellerWarehouseOptions.pending, (state) => {
        state.loadingSellerWarehouseOptions = true;
        state.error = null;
      })
      .addCase(fetchSellerWarehouseOptions.fulfilled, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = action.payload;
      })
      .addCase(fetchSellerWarehouseOptions.rejected, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = [];
        if (action.payload) state.error = action.payload;
      })

      // The signed-in seller's own warehouses, into the same slot
      .addCase(fetchOwnWarehouseOptions.pending, (state) => {
        state.loadingSellerWarehouseOptions = true;
        // Options left by another form must not be pickable while these load.
        state.sellerWarehouseOptions = [];
        state.error = null;
      })
      .addCase(fetchOwnWarehouseOptions.fulfilled, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = action.payload;
      })
      .addCase(fetchOwnWarehouseOptions.rejected, (state, action) => {
        state.loadingSellerWarehouseOptions = false;
        state.sellerWarehouseOptions = [];
        if (action.payload) state.error = action.payload;
      })

      // Creation wizard
      .addCase(createSeller.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createSeller.fulfilled, (state, action) => {
        state.creating = false;
        state.createdSeller = action.payload;
      })
      .addCase(createSeller.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload;
      })

      .addCase(checkSellerEmail.pending, (state, action) => {
        state.emailCheck = {
          email: action.meta.arg,
          status: 'checking',
          available: null,
        };
      })
      .addCase(checkSellerEmail.fulfilled, (state, action) => {
        // Answers can land out of order while the field is being typed. Only
        // the one for the address currently under check may write the result.
        if (action.payload.email !== state.emailCheck.email) return;
        state.emailCheck.status = 'done';
        state.emailCheck.available = action.payload.available;
      });
  }
});

export const {
  setSearchFilter,
  setPage,
  setRowsPerPage,
  clearError,
  clearSellerWarehouseOptions,
  clearSellerCreation,
} = sellersSlice.actions;

/**
 * The sellers of one operator, as options.
 *
 * Only the staff's screens ask for this: its roster is the whole platform, so
 * the list is only usable once it is narrowed by whoever owns it.
 *
 * @param {number|string|null} adminId - `'all'` or nothing returns everything.
 */
export const selectSellerOptionsByAdmin = (adminId) => (state) => {
  const options = state.sellers.sellerOptions;
  if (!adminId || adminId === 'all') return options;

  return options.filter((option) => String(option.adminId) === String(adminId));
};

/**
 * The companies of one operator, for the second step of the staff's ladder:
 * everything → one operator → one of its companies.
 *
 * Built from the sellers because `Company` does not carry its admin — the link
 * only exists through the seller in between. Without an operator it answers
 * nothing, and the screen falls back to the catalog the backend sends.
 */
export const selectCompanyOptionsByAdmin = (adminId) => (state) => {
  if (!adminId || adminId === 'all') return [];

  const byId = new Map();
  state.sellers.sellerOptions.forEach((option) => {
    if (String(option.adminId) !== String(adminId) || !option.companyId) return;
    if (!byId.has(option.companyId)) byId.set(option.companyId, option.label);
  });

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
};

const persistConfig = {
  key: 'sellers',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, sellersSlice.reducer);
